/**
 * unifiedInbox/loadUnifiedInbox — Firestore-Loader für die Unified Inbox
 * (architecture-ux-restructure.md §6.4).
 *
 * Kapselt ALLE Reads für die drei Erfassungs-Kanäle + den Verbucht-Index in
 * einer Funktion — Seiten (W5.2) rufen ausschließlich diese Funktion auf,
 * nie direkt Firestore. `Promise.all` über:
 *   (a) `received_invoices` — Full-Query (Muster `ReceivedInvoicesPage.tsx:56-60`).
 *   (b) `inbound_documents` — paginiert (Muster `InboxPage.tsx:142-152`);
 *       initial 2 Seiten à 25 (ein `limit(50)`-Read ist bei identischem
 *       Composite-Index `userId ASC, receivedAt DESC` bit-identisch zu zwei
 *       sequenziellen `limit(25)`-Reads, aber ein Round-Trip statt zwei).
 *   (c) `scanned_receipts` — `getScannedReceiptsForUser(scope)`.
 *   (d) `expenses` — `getExpensesForUser(scope)` → `buildExpenseLinkIndex`.
 *
 * "Mehr laden" (Folge-Aufruf mit `opts.inboundCursor`) lädt NUR eine weitere
 * inbound-Seite (25) — `received_invoices`/`scanned_receipts` werden NICHT
 * erneut geladen (sie sind bereits vollständig); `expenses` wird trotzdem
 * erneut gelesen, weil der zurückgegebene `ExpenseLinkIndex` gebraucht wird,
 * um den Status der NEU geladenen inbound-Docs korrekt abzuleiten (akzeptierte
 * Read-Amplification, §6.4 — identisches Lastprofil wie ein heutiger Besuch
 * von `loadReconcileData`). Der Aufrufer (Seite) mischt die Fortsetzungs-
 * `items` mit der bereits geladenen Liste selbst via `mergeAndSort`.
 *
 * @module unifiedInbox/loadUnifiedInbox
 */

import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
  DocumentData,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import type { ReceivedInvoice } from '../../types/receivedInvoiceTypes';
import type { InboundDocument } from '../../types/inboundTypes';
import { getScannedReceiptsForUser } from '../../api/scannedReceiptsApi';
import { getExpensesForUser } from '../../api/expensesApi';
import { buildExpenseLinkIndex, mapInbound, mapReceived, mapScanned, mergeAndSort } from './mappers';
import type { UnifiedInboxItem } from './types';

/** Page size for the paginated `inbound_documents` query (Muster `InboxPage.tsx`). */
const INBOUND_PAGE_SIZE = 25;

/** Number of inbound pages loaded on the INITIAL call (no cursor) — §6.4. */
const INBOUND_INITIAL_PAGES = 2;

/** Options for a continuation ("Mehr laden") call. */
export interface LoadUnifiedInboxOptions {
  /**
   * Cursor returned by a previous {@link loadUnifiedInbox} call's
   * `inboundCursor`. When provided, this call loads exactly ONE further
   * inbound page and returns ONLY the newly-mapped inbound items — it does
   * NOT re-read/re-return `received_invoices`/`scanned_receipts` items.
   * `undefined`/`null` ⇒ initial call (loads everything).
   */
  inboundCursor?: QueryDocumentSnapshot<DocumentData> | null;
}

/** Result of {@link loadUnifiedInbox}. */
export interface LoadUnifiedInboxResult {
  /**
   * On the initial call: ALL items from all three sources, merged+sorted.
   * On a continuation call: ONLY the newly-loaded inbound items (caller
   * merges with the previously-held list, e.g. via `mergeAndSort`).
   */
  items: UnifiedInboxItem[];

  /** Whether another inbound page is available beyond this call's batch. */
  hasMoreInbound: boolean;

  /** Cursor to pass as `opts.inboundCursor` for the next "Mehr laden" call, or `null` if exhausted. */
  inboundCursor: QueryDocumentSnapshot<DocumentData> | null;
}

/** Loads the full, sorted `received_invoices` list (Muster `ReceivedInvoicesPage.tsx:56-60`). */
async function loadAllReceived(scopeUid: string): Promise<ReceivedInvoice[]> {
  const q = query(
    collection(db, 'received_invoices'),
    where('userId', '==', scopeUid),
    orderBy('uploadedAt', 'desc'),
  );
  const snap = await getDocs(q);
  return snap.docs.map(
    (d): ReceivedInvoice => ({ id: d.id, ...(d.data() as Omit<ReceivedInvoice, 'id'>) }),
  );
}

/**
 * Loads one bounded batch of `inbound_documents` (Muster `InboxPage.tsx:142-152`).
 *
 * @param scopeUid effective tenant scope.
 * @param afterCursor `undefined` ⇒ first page; otherwise continues after this doc.
 * @param pageLimit total document count to fetch in this call (`INBOUND_PAGE_SIZE`
 *   for a continuation call, `INBOUND_PAGE_SIZE * INBOUND_INITIAL_PAGES` initially).
 */
async function loadInboundBatch(
  scopeUid: string,
  afterCursor: QueryDocumentSnapshot<DocumentData> | undefined,
  pageLimit: number,
): Promise<{
  docs: InboundDocument[];
  cursor: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}> {
  const colRef = collection(db, 'inbound_documents');
  const q = afterCursor
    ? query(
        colRef,
        where('userId', '==', scopeUid),
        orderBy('receivedAt', 'desc'),
        startAfter(afterCursor),
        limit(pageLimit),
      )
    : query(
        colRef,
        where('userId', '==', scopeUid),
        orderBy('receivedAt', 'desc'),
        limit(pageLimit),
      );
  const snap = await getDocs(q);
  const docs = snap.docs.map(
    (d): InboundDocument => ({ id: d.id, ...(d.data() as Omit<InboundDocument, 'id'>) }),
  );
  return {
    docs,
    cursor: snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null,
    hasMore: snap.docs.length === pageLimit,
  };
}

/**
 * Loads the Unified Inbox for a tenant scope — §6.4.
 *
 * @param scopeUid effective tenant scope (`effectiveScopeUid` from `AuthContext`).
 * @param opts optional continuation options (`inboundCursor` for "Mehr laden").
 * @returns items (initial: all sources merged+sorted; continuation: only the
 *   new inbound batch), `hasMoreInbound`, and the next `inboundCursor`.
 * @throws propagates any underlying Firestore/API read error (fail-fast — no
 *   silent partial-result swallowing at this layer; the caller/page decides
 *   how to degrade per-source, §7 Error-Handling-Strategie).
 */
export async function loadUnifiedInbox(
  scopeUid: string,
  opts?: LoadUnifiedInboxOptions,
): Promise<LoadUnifiedInboxResult> {
  const continuationCursor = opts?.inboundCursor ?? null;

  if (continuationCursor) {
    const [{ docs: inboundDocs, cursor, hasMore }, expenses] = await Promise.all([
      loadInboundBatch(scopeUid, continuationCursor, INBOUND_PAGE_SIZE),
      getExpensesForUser(scopeUid),
    ]);
    const index = buildExpenseLinkIndex(expenses);
    const items = inboundDocs.map((doc) => mapInbound(doc, index));

    return { items, hasMoreInbound: hasMore, inboundCursor: cursor };
  }

  const [received, { docs: inboundDocs, cursor, hasMore }, scanned, expenses] = await Promise.all([
    loadAllReceived(scopeUid),
    loadInboundBatch(scopeUid, undefined, INBOUND_PAGE_SIZE * INBOUND_INITIAL_PAGES),
    getScannedReceiptsForUser(scopeUid),
    getExpensesForUser(scopeUid),
  ]);

  const index = buildExpenseLinkIndex(expenses);

  const items = mergeAndSort([
    received.map((doc) => mapReceived(doc, index)),
    inboundDocs.map((doc) => mapInbound(doc, index)),
    scanned.map((doc) => mapScanned(doc, index)),
  ]);

  return { items, hasMoreInbound: hasMore, inboundCursor: cursor };
}
